/**
 * Dialog for renaming files with path validation
 */
import { useState, useEffect } from 'react';
import { Edit3, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import type { File } from '@phrasewright/types';
import { useAppSelector } from '@/store/hooks';

interface RenameFileDialogProps {
  open: boolean;
  onClose: () => void;
  onRename: (newPath: string, newName: string) => Promise<void>;
  originalFile: File;
}

export function RenameFileDialog({
  open,
  onClose,
  onRename,
  originalFile,
}: RenameFileDialogProps) {
  const { files } = useAppSelector((state) => state.files);
  const [newPath, setNewPath] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Initialize with original path when dialog opens
  useEffect(() => {
    if (open && originalFile) {
      setNewPath(originalFile.path || originalFile.name);
      setError('');
    }
  }, [open, originalFile]);

  const newName = newPath.split('/').pop() || '';

  // Validate the new path
  const validatePath = (path: string): string | null => {
    if (!path.trim()) {
      return 'Path cannot be empty';
    }

    if (path === (originalFile.path || originalFile.name)) {
      return 'New path must be different from original';
    }

    if (files.some((f) => f.path === path)) {
      return 'A file with this path already exists';
    }

    if (!/^[a-zA-Z0-9._/-]+$/.test(path)) {
      return 'Path contains invalid characters';
    }

    if (!path.endsWith('.md') && !path.endsWith('.txt')) {
      return 'File must have .md or .txt extension';
    }

    // Check if renaming would break directory structure
    const originalPath = originalFile.path || originalFile.name;
    const originalDir = originalPath.substring(
      0,
      originalPath.lastIndexOf('/')
    );
    const newDir = path.substring(0, path.lastIndexOf('/'));

    if (originalDir !== newDir) {
      return 'Moving files between directories is not supported yet';
    }

    return null;
  };

  const handlePathChange = (value: string) => {
    setNewPath(value);
    const validationError = validatePath(value);
    setError(validationError || '');
  };

  const handleRename = async () => {
    const validationError = validatePath(newPath);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await onRename(newPath, newName);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename file');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError('');
      onClose();
    }
  };

  const isValid = !error && newPath.trim() && newPath !== originalFile?.path;

  // Check for potential impacts
  const referencingFiles = files.filter(
    (f) =>
      f.id !== originalFile?.id &&
      (f.content.includes(`@${originalFile?.path}`) ||
        f.content.includes(`@${originalFile?.name}`))
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-4 w-4" />
            Rename File
          </DialogTitle>
          <DialogDescription>
            Change the name and path of "{originalFile?.name}".
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Original file info */}
          <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
            <div className="flex-1">
              <div className="font-medium text-sm">{originalFile?.name}</div>
              <div className="text-xs text-muted-foreground">
                {originalFile?.path}
              </div>
            </div>
            <Badge variant="secondary">{originalFile?.metadata.type}</Badge>
          </div>

          {/* New path input */}
          <div className="space-y-2">
            <label htmlFor="newPath" className="text-sm font-medium">
              New file path
            </label>
            <Input
              id="newPath"
              value={newPath}
              onChange={(e) => handlePathChange(e.target.value)}
              placeholder="Enter new file path..."
              disabled={isLoading}
            />
            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}
          </div>

          {/* Warning about references */}
          {referencingFiles.length > 0 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-center gap-2 text-sm text-yellow-800">
                <AlertCircle className="h-4 w-4" />
                <strong>Warning:</strong>
              </div>
              <p className="text-xs text-yellow-700 mt-1">
                This file is referenced by {referencingFiles.length} other file
                {referencingFiles.length !== 1 ? 's' : ''}. Renaming may break
                those references. You'll need to update them manually.
              </p>
              <div className="mt-2">
                <details className="text-xs">
                  <summary className="cursor-pointer text-yellow-800 font-medium">
                    Show referencing files
                  </summary>
                  <ul className="mt-1 space-y-1">
                    {referencingFiles.map((file) => (
                      <li key={file.id} className="text-yellow-700">
                        {file.path}
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            </div>
          )}

          {/* Preview */}
          {newName && !error && newPath !== originalFile?.path && (
            <div className="p-3 bg-green-50 rounded-md border">
              <div className="text-sm font-medium text-green-900">Preview</div>
              <div className="text-xs text-green-700 mt-1">
                New name: {newName}
                <br />
                New path: {newPath}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleRename}
            disabled={!isValid || isLoading}
            className="min-w-[100px]"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Renaming...
              </div>
            ) : (
              <>
                <Edit3 className="h-4 w-4 mr-2" />
                Rename
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
